/**
 * ML Readiness Metric Types - Client Side
 * ============================================================================
 * Client-side type definitions for the ML Readiness Metric Explanation Drawer.
 * These mirror the server-side types for API responses.
 *
 * Related Files:
 * - server/mlReadiness/metricTypes.ts (server types)
 * - client/components/mlReadiness/MLReadinessMetricDrawer.tsx (consumer)
 * - client/components/mlReadiness/useMLMetricDrawer.ts (API hook)
 *
 * @version 1.0.0
 * @created 2026-01-29
 * ============================================================================
 */

// ============================================================================
// Metric IDs
// ============================================================================

/**
 * Union type of all ML Readiness metric identifiers
 */
export type MLReadinessMetricId =
  // Primary Dimensions
  | 'overall_readiness'
  | 'sufficiency'
  | 'bias'
  | 'feature_engineering'
  // Sufficiency Sub-Metrics
  | 'sufficiency_volume'
  | 'sufficiency_coverage'
  | 'sufficiency_balance'
  // Bias Sub-Metrics
  | 'bias_demographic'
  | 'bias_outcome'
  | 'bias_feature'
  // Privacy Metrics
  | 'privacy'
  | 'privacy_kanonymity'
  | 'privacy_reidentification';

/**
 * Human-readable display names for each metric
 */
export const METRIC_DISPLAY_NAMES: Record<MLReadinessMetricId, string> = {
  overall_readiness: 'Overall ML Readiness',
  sufficiency: 'Data Sufficiency',
  bias: 'Bias Detection',
  feature_engineering: 'Feature Engineering',
  sufficiency_volume: 'Sample Volume',
  sufficiency_coverage: 'Data Coverage',
  sufficiency_balance: 'Class Balance',
  bias_demographic: 'Demographic Bias',
  bias_outcome: 'Outcome Bias',
  bias_feature: 'Feature/Proxy Bias',
  privacy: 'Privacy Risk',
  privacy_kanonymity: 'k-Anonymity',
  privacy_reidentification: 'Re-Identification Risk',
};

/**
 * Icons for each metric
 */
export const METRIC_ICONS: Record<MLReadinessMetricId, string> = {
  overall_readiness: '🤖',
  sufficiency: '📊',
  bias: '⚖️',
  feature_engineering: '🔧',
  sufficiency_volume: '📈',
  sufficiency_coverage: '🎯',
  sufficiency_balance: '⚖️',
  bias_demographic: '👥',
  bias_outcome: '📉',
  bias_feature: '🔗',
  privacy: '🔒',
  privacy_kanonymity: '🛡️',
  privacy_reidentification: '🔍',
};

// ============================================================================
// Regulatory Citations
// ============================================================================

/**
 * A citation from an AI/ML regulatory framework or standard.
 *
 * v2.5 (DC-1666): `source` defaults to 'framework' when absent. When 'internal'
 * the citation is an approved clause from the customer's own governance document
 * and carries provenance for the [INTERNAL] drawer card.
 */
export interface RegulatoryMLCitation {
  framework: string;
  document: string;
  section: string;
  text: string;
  url?: string;

  // v2.5 DC-1666: source discriminator + internal provenance
  source?: 'framework' | 'internal';
  documentId?: string;
  documentTitle?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  mappingId?: string;
  confidence?: number;
}

// ============================================================================
// Score Thresholds
// ============================================================================

/**
 * Threshold configuration for score interpretation
 */
export interface MLScoreThresholds {
  excellent: number;
  good: number;
  poor: number;
}

// ============================================================================
// Findings
// ============================================================================

/**
 * A specific finding from the analysis
 */
export interface MLMetricFinding {
  type: 'critical' | 'warning' | 'info' | 'positive';
  message: string;
  columns?: string[];
  value?: number | string;
  details?: string;
}

// ============================================================================
// Sub-Metrics
// ============================================================================

/**
 * Value of a sub-metric extracted from analysis results
 */
export interface MLSubMetricValue {
  id: string;
  name: string;
  value: number | string | null;
  unit: string;
  status: 'excellent' | 'good' | 'warning' | 'critical' | 'not_available';
  details?: string;
}

// ============================================================================
// Recommendations
// ============================================================================

/**
 * A specific recommendation for improvement
 */
export interface MLMetricRecommendation {
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impactEstimate?: number;
}

// ============================================================================
// Main Response Type
// ============================================================================

/**
 * Full detail response for an ML Readiness metric
 */
export interface MLReadinessMetricDetail {
  metricId: MLReadinessMetricId;
  name: string;
  icon: string;

  // Assessment Result
  score: number;
  rating: 'excellent' | 'good' | 'poor' | 'critical';
  assessmentSummary: string;

  // Key Findings
  findings: MLMetricFinding[];

  // Educational Content
  whatItMeasures: string;
  whyItMatters: string;
  thresholds: MLScoreThresholds;

  // Sub-Metrics Breakdown
  subMetrics: MLSubMetricValue[];

  // Citations
  citations: RegulatoryMLCitation[];

  // Recommendations
  recommendations: MLMetricRecommendation[];

  // Metadata
  wasAnalyzed: boolean;
  notAnalyzedReason?: string;
}

// ============================================================================
// API Response Type
// ============================================================================

/**
 * API response type for the metric detail endpoint
 */
export interface GetMLMetricDetailResponse {
  success: boolean;
  data?: MLReadinessMetricDetail;
  error?: string;
}

// ============================================================================
// Drawer Event Types
// ============================================================================

/**
 * Custom event detail for ML metric click events
 */
export interface MLMetricClickEventDetail {
  metricId: MLReadinessMetricId;
  score: number;
}

/**
 * Declare custom event type on Window
 */
declare global {
  interface WindowEventMap {
    'ml-metric-click': CustomEvent<MLMetricClickEventDetail>;
  }

  interface Window {
    /** Mount the ML Metric Drawer for standalone (vanilla JS) mode */
    mountMLMetricDrawer?: (jobId: string) => void;
    /** Unmount the ML Metric Drawer */
    unmountMLMetricDrawer?: () => void;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * All valid metric IDs
 */
export const ALL_METRIC_IDS: MLReadinessMetricId[] = [
  'overall_readiness',
  'sufficiency',
  'bias',
  'feature_engineering',
  'sufficiency_volume',
  'sufficiency_coverage',
  'sufficiency_balance',
  'bias_demographic',
  'bias_outcome',
  'bias_feature',
  'privacy',
  'privacy_kanonymity',
  'privacy_reidentification',
];

/**
 * Check if a string is a valid metric ID
 */
export function isValidMetricId(id: string): id is MLReadinessMetricId {
  return ALL_METRIC_IDS.includes(id as MLReadinessMetricId);
}

/**
 * Get CSS class for rating
 */
export function getRatingClass(rating: 'excellent' | 'good' | 'poor' | 'critical'): string {
  switch (rating) {
    case 'excellent':
      return 'ml-metric__status--excellent';
    case 'good':
      return 'ml-metric__status--good';
    case 'poor':
      return 'ml-metric__status--poor';
    case 'critical':
      return 'ml-metric__status--critical';
    default:
      return '';
  }
}

/**
 * Get display label for rating
 */
export function getRatingLabel(rating: 'excellent' | 'good' | 'poor' | 'critical'): string {
  switch (rating) {
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'poor':
      return 'Needs Improvement';
    case 'critical':
      return 'Critical';
    default:
      return rating;
  }
}

/**
 * Get CSS class for finding type
 */
export function getFindingClass(type: MLMetricFinding['type']): string {
  switch (type) {
    case 'critical':
      return 'ml-metric__finding--critical';
    case 'warning':
      return 'ml-metric__finding--warning';
    case 'info':
      return 'ml-metric__finding--info';
    case 'positive':
      return 'ml-metric__finding--positive';
    default:
      return '';
  }
}

/**
 * Get icon for finding type
 */
export function getFindingIcon(type: MLMetricFinding['type']): string {
  switch (type) {
    case 'critical':
      return '❌';
    case 'warning':
      return '⚠️';
    case 'info':
      return 'ℹ️';
    case 'positive':
      return '✅';
    default:
      return '';
  }
}

/**
 * Get CSS class for sub-metric status
 */
export function getSubMetricStatusClass(status: MLSubMetricValue['status']): string {
  switch (status) {
    case 'excellent':
      return 'ml-metric__submetric--excellent';
    case 'good':
      return 'ml-metric__submetric--good';
    case 'warning':
      return 'ml-metric__submetric--warning';
    case 'critical':
      return 'ml-metric__submetric--critical';
    case 'not_available':
      return 'ml-metric__submetric--na';
    default:
      return '';
  }
}

/**
 * Get CSS class for priority
 */
export function getPriorityClass(priority: MLMetricRecommendation['priority']): string {
  switch (priority) {
    case 'high':
      return 'ml-metric__priority--high';
    case 'medium':
      return 'ml-metric__priority--medium';
    case 'low':
      return 'ml-metric__priority--low';
    default:
      return '';
  }
}

/**
 * Dispatch an ML metric click event
 * Used by vanilla JS code to trigger the React drawer
 */
export function dispatchMLMetricClick(
  metricId: MLReadinessMetricId,
  score: number
): void {
  const event = new CustomEvent('ml-metric-click', {
    detail: { metricId, score },
    bubbles: true,
    cancelable: true,
  });
  window.dispatchEvent(event);
}
